/**
 * pillages system — v0M9 (2026-07-27) per MECHANICS.md §2 v0M9 amendment
 * ("Minor settlements cannot be captured; only pillaged").
 *
 * Peer of `sieges.ts`. Runs each tick after sieges (established siege
 * outcomes are visible in state) and before economy (so pillage flags
 * gate this-tick structure income). Applies only to minor Settlements
 * (`isMinor === true`); majors are handled by sieges.ts.
 *
 * Per-tick per-minor logic:
 *
 *   1. If `pillageState` is set (timer running):
 *      a. Sieger destroyed / disbanded → clear pillageState (raider
 *         model — timer requires continuous enemy presence).
 *      b. Sieger in a field battle → clear (same reason as sieges break
 *         condition b — treat as "no longer draining the timer").
 *      c. No hostile army remains in the minor's ZoC → clear.
 *      d. Otherwise: increment `daysElapsed` by DAYS_PER_TICK.
 *      e. If `daysElapsed >= MINOR_SETTLEMENT_PILLAGE_DAYS`:
 *         mark `pillaged = true`, clear pillageState, clear any repair
 *         state (an ongoing repair is invalidated by a completed pillage).
 *
 *   2. If `pillageState` is null and no repairState is running:
 *      Find the lowest-id hostile army in the minor's ZoC that is
 *      not itself in a battle. If found, open a fresh pillageState.
 *      Skips if minor already has a garrison (v0M9 MVP assumes minors
 *      are always ungarrisoned — see design note below).
 *
 * **v0M9 MVP scope note.** Michael's spec: minors start with 0
 * garrison; Armies are recruited at Province Capitals per
 * MECHANICS.md §3.1 (no recruit path at minors). Consequence: at
 * v0M9 the "garrisoned minor" branch is unreachable in production
 * play. The garrison-present skip below is a defensive gate so a
 * future v0M10 that wires minor-garrison recruit can layer a proper
 * battle-first-then-pillage path without changing this system's
 * shipped behavior.
 *
 * Ownership never changes at pillage. Only `Settlement.pillaged` +
 * structure income (economy.ts) are affected. Repair is the only path
 * back — see `repair.ts`.
 */

import type { Command } from './commands';
import type { Army, GameState, Settlement } from './state';
import type { RngState } from './rng';
import type { SimContext } from './sim-context';
import {
  sortedIds,
  TICKS_PER_GAME_DAY,
  MINOR_SETTLEMENT_PILLAGE_DAYS,
} from './state';
import { getDialNumber, loadDials } from '../data/dials';
import { createSiegeBattleFromArmies } from './battle-factory';
// v0M13.1 (2026-07-30) — entry gate. A zero-eligible attacker triggers
// destruction (MFix1.5 pattern) instead of spawning a battle it cannot
// meaningfully fight. Consistent with zoc-detection's weak-army handling.
import { armyHasEligibleCohorts } from './battle-entry-gate';
import { destroyAllCohortsOfArmy, disbandArmy } from './battle-conclusion';

// ---------------------------------------------------------------------------
// Dials
// ---------------------------------------------------------------------------

const PILLAGE_DIALS = loadDials();

/**
 * Best-effort dial lookup: returns the xlsx value if authored, else the
 * code-side fallback constant. Pattern matches sieges.ts safeDialNumber.
 */
function safeDialNumber(name: string, fallback: number): number {
  try { return getDialNumber(PILLAGE_DIALS, name); } catch { return fallback; }
}

const PILLAGE_DAYS = safeDialNumber('minor_settlement_pillage_days', MINOR_SETTLEMENT_PILLAGE_DAYS);
// Reuse the settlement ZoC dial + 0.5 slop for float-position tolerance,
// matching sieges.ts SIEGE_ADJACENCY_RADIUS. A minor's ZoC is the same
// Chebyshev-square-around-cell shape as a major's ZoC.
const PILLAGE_ZOC_RADIUS = safeDialNumber('settlement_zoc_radius', 2) + 0.5;

const DAYS_PER_TICK = 1 / TICKS_PER_GAME_DAY;

// ---------------------------------------------------------------------------
// System entry
// ---------------------------------------------------------------------------

export function pillagesSystem(
  state: GameState,
  _commands: readonly Command[],
  _rng: RngState,
  context: SimContext,
): GameState {
  const settlementIds = sortedIds(state.settlements);
  for (const sid of settlementIds) {
    const settlement = state.settlements.get(sid);
    if (!settlement) continue;
    if (!settlement.isMinor) continue;

    if (settlement.pillageState != null) {
      updateActivePillage(state, settlement);
    } else if (settlement.repairState == null) {
      // Only try to open a fresh pillage when no repair is in flight.
      // (If repair is running, letting a pillage timer start too would
      // create tug-of-war state — the enemy first has to break the
      // repair effort by chasing the owner off; then pillage can open
      // on the next tick when repairState is null.)
      maybeOpenPillage(state, settlement, context);
    }
  }
  return state;
}

// ---------------------------------------------------------------------------
// Active pillage — tick + break + completion
// ---------------------------------------------------------------------------

function updateActivePillage(state: GameState, settlement: Settlement): void {
  const p = settlement.pillageState;
  if (p == null) return;

  // Any-enemy-in-ZoC presence check (raider model — timer continues
  // even if the original sieger left, so long as another enemy remains
  // per Michael's design lock).
  const anyEnemy = findLowestIdHostileInZoc(state, settlement);
  if (!anyEnemy) {
    // Break: no hostile army in ZoC — reset timer per raider model.
    settlement.pillageState = null;
    return;
  }

  // Countdown.
  p.daysElapsed += DAYS_PER_TICK;
  if (p.daysElapsed >= PILLAGE_DAYS) {
    // Completion: mark pillaged, clear timer, invalidate any repair.
    settlement.pillaged = true;
    settlement.pillageState = null;
    settlement.repairState = null;
  }
}

// ---------------------------------------------------------------------------
// Trigger evaluation
// ---------------------------------------------------------------------------

function maybeOpenPillage(state: GameState, settlement: Settlement, context: SimContext): void {
  const hostile = findLowestIdHostileInZoc(state, settlement);
  if (!hostile) return;

  // v0M9-followup.3 (2026-07-27) — if a garrison is present, spawn a
  // garrison-siege battle instead of opening the pillage timer directly.
  // The garrison fights the attacker; on garrison defeat, pillages.ts
  // next tick sees an empty garrison and opens the timer via the
  // fall-through below. `captureCity` has an isMinor early-return so
  // an attacker victory does NOT flip province ownership.
  if (settlement.garrisonCohortIds.length > 0) {
    // Skip if attacker already in a battle (defensive).
    if (hostile.currentBattleId !== null) return;
    // v0M13.1 (2026-07-30) — zero-eligible attacker gets destroyed instead
    // of spawning a battle (MFix1.5 pattern; matches zoc-detection.ts).
    // Without this the sim spawns a battle that would immediately conclude
    // with the attacker as a phantom-side (no cohorts on the field) which
    // downstream conclusion logic isn't designed to handle cleanly.
    if (!armyHasEligibleCohorts(hostile, state)) {
      destroyAllCohortsOfArmy(state, hostile);
      disbandArmy(state, hostile);
      return;
    }
    // Spawn siege battle. Attacker army = hostile, target = minor
    // Settlement, defenderArmy = null (only the garrison defends).
    const battle = createSiegeBattleFromArmies(state, hostile, settlement, null, context);
    state.battles.set(battle.id, battle);
    return;
  }

  settlement.pillageState = {
    byFactionId: hostile.ownerFactionId,
    daysElapsed: 0,
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Find the lowest-id Army that is (a) hostile to the settlement's
 * province owner, (b) within pillage ZoC of the settlement cell, and
 * (c) not in a field battle. Returns null if none found.
 *
 * Hostility semantics: a minor's owner is its province owner. If the
 * province is unowned (`null`) — the wilderness case for minors in
 * wilderness provinces — every army counts as hostile (same as
 * sieges.ts neutral-city treatment).
 */
function findLowestIdHostileInZoc(state: GameState, settlement: Settlement): Army | null {
  const province = state.provinces.get(settlement.provinceId);
  if (!province) return null;
  const ownerFactionId = province.ownerFactionId;
  let ownerFaction: import('./state').Faction | null = null;
  if (ownerFactionId !== null) {
    const f = state.factions.get(ownerFactionId);
    if (!f) return null;
    ownerFaction = f;
  }

  for (const armyId of sortedIds(state.armies)) {
    const army = state.armies.get(armyId);
    if (!army) continue;
    if (army.currentBattleId !== null) continue;
    const armyFaction = state.factions.get(army.ownerFactionId);
    if (!armyFaction) continue;
    // Hostility: wilderness (ownerFaction null) = everyone hostile;
    // otherwise different-team = hostile.
    if (ownerFaction !== null && armyFaction.teamId === ownerFaction.teamId) continue;
    if (!isArmyInPillageZoc(army, settlement)) continue;
    return army;
  }
  return null;
}

function isArmyInPillageZoc(army: Army, settlement: Settlement): boolean {
  const dx = Math.abs(army.position.x - settlement.cell.x);
  const dy = Math.abs(army.position.y - settlement.cell.y);
  return Math.max(dx, dy) <= PILLAGE_ZOC_RADIUS;
}

// ---------------------------------------------------------------------------
// Internals (for tests)
// ---------------------------------------------------------------------------

export const _pillageInternals = {
  PILLAGE_ZOC_RADIUS,
  PILLAGE_DAYS,
  DAYS_PER_TICK,
};
